/**
 * Handler: select_item event
 *
 * Built by the SDK rather than the CDN's gaSelectItem/gtmSelectItem: those
 * take no list name or position and their list is fixed when the landing
 * page initializes ("Homepage Products" for a home landing), so every later
 * selection in an SPA would report the landing page's list.
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import type { StorefrontProduct } from '../../types/storefront';
import { adaptProduct } from '../../adapters/product';
import { extractNumericId } from '../../adapters/ids';
import { generateEventId, gtmMarketFields } from '../utils';
import { sendGA4Fallback, mapGA4Item } from '../fallbacks/ga4';

export function handleSelectItem(
  analyzify: WindowAnalyzify,
  product: StorefrontProduct,
  listName: string,
  index?: number,
  listId?: string | number
): void {
  const { product: adaptedProduct, variant, taxonomy } = adaptProduct(product);
  const eventId = generateEventId();
  const position = typeof index === 'number' && Number.isFinite(index) ? index : 0;

  // List identity: the given list, else the collection the visitor is on.
  const collection = (analyzify as any).shopify_template === 'collection'
    ? (analyzify as any).getCollectionObj
    : null;
  const resolvedListName = listName?.trim() || collection?.title || null;
  const resolvedListId =
    (listId !== undefined && listId !== null && listId !== ''
      ? (extractNumericId(listId as string | number) || String(listId))
      : collection?.id) || null;
  const listHandle = collection && (!listName || listName === collection.title) ? collection.handle || null : null;

  const price = analyzify.formatPrice?.(variant.price ?? adaptedProduct.price, true) || 0;
  const compareAtPrice = adaptedProduct.compare_at_price
    ? (analyzify.formatPrice?.(adaptedProduct.compare_at_price, true) || 0)
    : 0;
  const discount = compareAtPrice > 0 && compareAtPrice !== price ? compareAtPrice - price : 0;

  // GA4 (per-destination event flags and product ID formats)
  sendGA4Fallback(analyzify, 'select_item', {
    currency: analyzify.currency || 'USD',
    value: price,
    item_list_id: resolvedListId,
    item_list_name: resolvedListName,
    event_id: eventId,
    items: [mapGA4Item(analyzify, adaptedProduct, variant, position, resolvedListId, resolvedListName)],
  });

  // GTM — same payload shape as the CDN's gtmSelectItem
  const win = window as any;
  if (Array.isArray(win.dataLayer) && analyzify.properties?.GTM?.status) {
    const market = analyzify.market;
    const dynamicCountry = market?.country || analyzify.feed_region || null;

    win.dataLayer.push({ ecommerce: null });
    win.dataLayer.push({
      event: 'ee_select_item',
      ss_event_id: eventId,
      ecommerce: {
        analyzify_source: 'advanced',
        implementation_type: 'extension',
        is_b2b: (analyzify.shopify_customer as any)?.b2b ?? null,
        currency: analyzify.currency || null,
        value: price,
        cart_id: analyzify.cart_id || null,
        items: [{
          id: analyzify.getShopifyId?.(
            analyzify.feed_region || '',
            adaptedProduct.id,
            variant.id,
            variant.sku
          ) || null,
          dynamic_item_id: dynamicCountry
            ? analyzify.getShopifyId?.(String(dynamicCountry).toUpperCase(), adaptedProduct.id, variant.id) || null
            : null,
          item_pid: adaptedProduct.id?.toString() || null,
          item_id: adaptedProduct.id?.toString() || null,
          item_name: adaptedProduct.title?.trim() || null,
          item_list_name: resolvedListName,
          item_list_id: resolvedListId !== null ? String(resolvedListId) : null,
          item_list_handle: listHandle,
          item_barcode: variant.barcode || null,
          discount: analyzify.formatPrice?.(discount, false) || 0,
          price,
          item_category: adaptedProduct.type || null,
          item_brand: adaptedProduct.vendor?.trim() || null,
          item_variant: variant.title?.trim() || null,
          item_variant_id: variant.id?.toString() || null,
          item_sku: variant.sku || null,
          index: position,
          quantity: 1,
          // Same extras as the CDN's gtmSelectItem
          ...(analyzify.properties?.GTM?.enhanced_params ? {
            taxonomy_id: taxonomy?.id || null,
            taxonomy_name: taxonomy?.name || null,
            taxonomy_ancestors: taxonomy?.ancestors || null,
            tags: adaptedProduct.tags?.join(',') || null,
            options: adaptedProduct.options?.join(',') || null,
            variant_ids: adaptedProduct.variants?.map((v) => v?.id?.toString() || null).join(',') || null,
          } : {}),
          ...((analyzify as any).itemProps?.[String(adaptedProduct.id)] || {}),
        }],
      },
      ...gtmMarketFields(analyzify),
    });
  }
}
